import { useState, type MouseEvent } from "react";
import {
  AppBar,
  Box,
  Container,
  Divider,
  IconButton,
  Popover,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { LogOut, Menu, Moon, Settings, Sun } from "lucide-react";
import { NotificationBell } from "modules/notifications/ui/NotificationBell";
import oxyDtvmLogoColor from "shared/assets/oxy-dtvm-logo-color.png";
import oxyDtvmLogoWhite from "shared/assets/oxy-dtvm-logo-white.png";
import { useThemeMode } from "shared/theme/ThemeModeContext";

interface HeaderProps {
  userEmail: string;
  userName: string;
  userAvatarSrc: string | null;
  onOpenNavigation: () => void;
  onOpenSettings: () => void;
  onSignOut: () => void;
}

export function Header({
  userEmail,
  userName,
  userAvatarSrc,
  onOpenNavigation,
  onOpenSettings,
  onSignOut,
}: HeaderProps) {
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const { mode, toggleMode } = useThemeMode();
  const isDark = mode === "dark";
  const userInitial = userName.charAt(0).toUpperCase();
  const isUserMenuOpen = Boolean(userMenuAnchor);

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setUserMenuAnchor(null);
  };

  const handleSettingsClick = () => {
    handleCloseUserMenu();
    onOpenSettings();
  };

  const handleSignOutClick = () => {
    handleCloseUserMenu();
    onSignOut();
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        backgroundImage: "none",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
      })}
    >
      <Container maxWidth={false} sx={{ px: { xs: 2, lg: 3.5 } }}>
        <Toolbar disableGutters sx={{ minHeight: 78, gap: 1.5 }}>
          <IconButton
            onClick={onOpenNavigation}
            aria-label="Abrir menu do portal"
            sx={(theme) => ({
              display: { xs: "inline-flex", lg: "none" },
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              color: theme.palette.text.primary,
            })}
          >
            <Menu size={20} />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.6, minWidth: 0 }}>
            <Box
              component="img"
              src={isDark ? oxyDtvmLogoWhite : oxyDtvmLogoColor}
              alt="OXY DTVM"
              sx={{
                display: "block",
                width: { xs: 116, sm: 128 },
                height: "auto",
                flexShrink: 0,
              }}
            />
          </Box>

          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5 },
            }}
          >
            <NotificationBell />

            <Box
              component="button"
              type="button"
              onClick={handleOpenUserMenu}
              aria-haspopup="true"
              aria-expanded={isUserMenuOpen}
              aria-label="Abrir menu do usuário"
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                px: 1.1,
                py: 0.8,
                borderRadius: 999,
                bgcolor: isUserMenuOpen
                  ? alpha(theme.palette.primary.main, 0.12)
                  : alpha(theme.palette.primary.main, 0.04),
                border: "1px solid",
                borderColor: isUserMenuOpen ? theme.palette.primary.main : theme.palette.divider,
                cursor: "pointer",
                font: "inherit",
                color: theme.palette.text.primary,
                transition: "all 160ms ease",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                },
              })}
            >
              <Box
                sx={(theme) => ({
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  overflow: "hidden",
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.92rem",
                  fontWeight: 700,
                })}
              >
                {userAvatarSrc ? (
                  <Box
                    component="img"
                    src={userAvatarSrc}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                ) : (
                  userInitial
                )}
              </Box>

              <Typography
                sx={(theme) => ({
                  display: { xs: "none", sm: "block" },
                  color: theme.palette.text.primary,
                  fontSize: "0.92rem",
                  fontWeight: 600,
                  lineHeight: 1.2,
                })}
              >
                {userName}
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </Container>

      <Popover
        open={isUserMenuOpen}
        anchorEl={userMenuAnchor}
        onClose={handleCloseUserMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: (theme) => ({
              mt: 1,
              minWidth: 240,
              p: 1.5,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 24px 48px rgba(0, 0, 0, 0.5)"
                  : "0 24px 48px rgba(15, 23, 42, 0.12)",
            }),
          },
        }}
      >
        <Box sx={{ px: 1, py: 0.5 }}>
          <Typography
            sx={(theme) => ({
              color: theme.palette.text.primary,
              fontSize: "0.92rem",
              fontWeight: 600,
            })}
          >
            {userName}
          </Typography>
          <Typography
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              fontSize: "0.78rem",
              wordBreak: "break-all",
            })}
          >
            {userEmail}
          </Typography>
        </Box>

        <Divider sx={(theme) => ({ my: 1, borderColor: theme.palette.divider })} />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Tooltip
            title={isDark ? "Ativar modo claro" : "Ativar modo escuro"}
            placement="left"
          >
            <IconButton
              onClick={toggleMode}
              aria-label={isDark ? "Ativar modo claro" : "Ativar modo escuro"}
              sx={(theme) => ({
                width: 40,
                height: 40,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                  borderColor: theme.palette.primary.main,
                },
              })}
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Configurações" placement="left">
            <IconButton
              onClick={handleSettingsClick}
              aria-label="Abrir configurações"
              sx={(theme) => ({
                width: 40,
                height: 40,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                  borderColor: theme.palette.primary.main,
                },
              })}
            >
              <Settings size={18} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Sair do portal" placement="left">
            <IconButton
              onClick={handleSignOutClick}
              aria-label="Sair do portal"
              sx={(theme) => ({
                width: 40,
                height: 40,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.error.main,
                bgcolor: alpha(theme.palette.error.main, 0.06),
                "&:hover": {
                  bgcolor: alpha(theme.palette.error.main, 0.14),
                  borderColor: theme.palette.error.main,
                },
              })}
            >
              <LogOut size={18} />
            </IconButton>
          </Tooltip>
        </Box>
      </Popover>
    </AppBar>
  );
}
